import type { MascotGameState, Need, Room, MiniGameKind } from './mascot-game-types'
import { sleepingEnergy } from './mascot-sleep'

export const ROOMS: {
  id: Room
  name: string
  icon: string
  title: string
  description: string
}[] = [
  {
    id: 'veterinary',
    name: 'Veterinário',
    icon: 'mdi:medical-bag',
    title: 'Clínica do bem.',
    description: 'Uma compra que apoia a entidade e cuida do seu companheiro.',
  },
  {
    id: 'home',
    name: 'Sala',
    icon: 'mdi:home-heart',
    title: 'Lar, doce lar.',
    description: 'Um carinho, uma brincadeira e a melhor companhia.',
  },
  {
    id: 'kitchen',
    name: 'Cozinha',
    icon: 'mdi:food-apple-outline',
    title: 'O que tem de bom?',
    description: 'Abra a despensa e escolha o próximo lanchinho.',
  },
  {
    id: 'bath',
    name: 'Cuidados',
    icon: 'mdi:shower-head',
    title: 'Hora de se cuidar.',
    description: 'Um banho de carinho para ficar bem de novo.',
  },
  {
    id: 'bedroom',
    name: 'Quarto',
    icon: 'mdi:weather-night',
    title: 'Sonhos bem fofinhos.',
    description: 'Apague a luz. A energia volta enquanto seu mascote descansa.',
  },
  {
    id: 'garden',
    name: 'Jardim',
    icon: 'mdi:sprout-outline',
    title: 'Cultive coisas boas.',
    description: 'Plante, acompanhe e colha com o seu companheiro.',
  },
  {
    id: 'arcade',
    name: 'Jogos',
    icon: 'mdi:controller-classic-outline',
    title: 'Vamos brincar?',
    description: 'Pequenas aventuras, grandes recordações.',
  },
  {
    id: 'wardrobe',
    name: 'Personalizar',
    icon: 'mdi:paw',
    title: 'Do seu jeitinho.',
    description: 'Uma nova cor, um acessório e uma casa com personalidade.',
  },
  {
    id: 'journal',
    name: 'Diário',
    icon: 'mdi:book-heart-outline',
    title: 'Nossa história.',
    description: 'Cada cuidado vira uma conquista.',
  },
  {
    id: 'community',
    name: 'Vizinhança',
    icon: 'mdi:account-group-outline',
    title: 'Uma turma do bem.',
    description: 'Conheça os mascotes dos maiores apoiadores desta campanha.',
  },
]

export const NEEDS: { id: Need; name: string; icon: string; room: Room }[] = [
  { id: 'satiety', name: 'Barriguinha', icon: 'mdi:food-apple', room: 'kitchen' },
  { id: 'cleanliness', name: 'Higiene', icon: 'mdi:water', room: 'bath' },
  { id: 'energy', name: 'Energia', icon: 'mdi:lightning-bolt', room: 'bedroom' },
  { id: 'joy', name: 'Diversão', icon: 'mdi:emoticon-happy', room: 'arcade' },
  { id: 'health', name: 'Saúde', icon: 'mdi:heart', room: 'veterinary' },
]

export const MINI_GAMES: {
  id: MiniGameKind
  name: string
  icon: string
  tag: string
  description: string
}[] = [
  {
    id: 'frisbee',
    name: 'Busca do frisbee',
    icon: '🥏',
    tag: 'NOVO · CORRIDA SEM PARAR',
    description:
      'Pule os obstáculos, pegue moedas pelo caminho e alcance o frisbee em 10 minutos!',
  },
  {
    id: 'catch',
    name: 'Chuva de frutas',
    icon: '🍎',
    tag: 'REFLEXOS · PRECISÃO',
    description:
      'Pegue as frutas enquanto o ritmo acelera. Cinco erros encerram a partida.',
  },
  {
    id: 'memory',
    name: 'Pares do jardim',
    icon: '🌻',
    tag: 'MEMÓRIA · TENTATIVAS LIMITADAS',
    description: 'Encontre os pares antes de acabar o tempo ou as tentativas.',
  },
  {
    id: 'runner',
    name: 'Trilha de aventura',
    icon: '🛹',
    tag: 'AGILIDADE · RITMO CRESCENTE',
    description: 'Encontre a faixa livre entre as pedras. Você tem três vidas!',
  },
  {
    id: 'sequence',
    name: 'Luzes musicais',
    icon: '🎵',
    tag: 'SEQUÊNCIA · CONTRA O TEMPO',
    description: 'Observe as cores e repita a melodia na ordem certa.',
  },
]

export function miniGamesForHabitat(jungle: boolean, sheep = false) {
  if (sheep)
    return MINI_GAMES.map((game) =>
      game.id === 'catch'
        ? {
            ...game,
            name: 'Colheita da fazendinha',
            icon: '🥕',
            description:
              'Pegue os vegetais fresquinhos enquanto o ritmo acelera. Cinco erros encerram a partida.',
          }
        : game.id === 'memory'
          ? { ...game, name: 'Pares da fazenda', icon: '🐑' }
          : game.id === 'runner'
            ? { ...game, name: 'Trilha do pasto', icon: '☘️' }
            : game
    )
  return jungle
    ? MINI_GAMES.map((game) =>
        game.id === 'catch'
          ? {
              ...game,
              name: 'Chuva de petiscos',
              icon: '🥩',
              description:
                'Pegue as porções de carne enquanto o ritmo acelera. Cinco erros encerram a partida.',
            }
          : game.id === 'memory'
            ? { ...game, name: 'Pares da floresta', icon: '🌿' }
            : game
      )
    : MINI_GAMES
}

/** Display projection only. The server independently advances and validates every action. */
export function projectNeeds(state: MascotGameState, now: number) {
  const hours = Math.min(72, Math.max(0, now - state.updated_at) / 3600)
  const needs = state.needs
  return {
    satiety: Math.max(5, needs.satiety - hours * 4),
    cleanliness: needs.cleanliness,
    energy: Math.max(
      5,
      Math.min(
        100,
        state.sleeping
          ? sleepingEnergy(needs.energy, state.updated_at, now)
          : needs.energy - hours * 5
      )
    ),
    joy: Math.max(5, needs.joy - hours),
    health: Math.max(
      Math.min(25, needs.health),
      needs.health - (Math.max(0, now - state.updated_at) * 75) / (15 * 86400)
    ),
  }
}

export function mascotMood(state: MascotGameState, now: number) {
  const needs = projectNeeds(state, now)
  if (state.sleeping) return 'Shhh… estou sonhando com você.'
  if (needs.health < 35) return 'Um cuidado me faria muito bem.'
  if (needs.satiety < 35) return 'Minha barriguinha está roncando…'
  if (needs.energy < 30) return 'Acho que preciso de uma soneca.'
  if (needs.cleanliness < 35) return 'Vamos brincar com as bolhas?'
  if (needs.joy < 35) return 'Que tal uma aventura juntos?'
  return 'Meu lugar favorito é aqui, com você!'
}

export function timeLabel(seconds: number) {
  const remaining = Math.max(0, Math.ceil(seconds))
  const hours = Math.floor(remaining / 3600)
  return `${hours ? `${hours.toString().padStart(2, '0')}:` : ''}${Math.floor(
    (remaining % 3600) / 60
  )
    .toString()
    .padStart(2, '0')}:${(remaining % 60).toString().padStart(2, '0')}`
}

const dailyClock = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export function gameDay(now: number) {
  return dailyClock.format(new Date(now * 1000))
}

export function dailyResetIn(now: number) {
  return Date.parse(`${gameDay(now)}T00:00:00-03:00`) / 1000 + 86400 - now
}
